import { useState, useEffect, useRef } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth';
import { auth } from '@/lib/firebase/config';
import { LoginPage, LoadingSpinner } from '@elka/ui';
import { timeGreeting } from '@elka/utils';
import OTPVerificationForm from './OTPVerificationForm';
import { normalizeIsraeliPhone } from '../utils/normalizePhone';
import { useAuth } from '../hooks/useAuth';

const RECAPTCHA_ID = 'recaptcha-widget';

export default function PhoneLoginForm() {
  const currentYear = new Date().getFullYear();
  // One-shot error surfaced by useAuth when a session was signed out for
  // lacking a tenantId claim (see useAuth.ts's pendingAuthError).
  const { authError } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      verifierRef.current?.clear();
      verifierRef.current = null;
    };
  }, []);

  const getVerifier = (): RecaptchaVerifier => {
    if (!verifierRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, RECAPTCHA_ID, { size: 'invisible' });
    }
    return verifierRef.current;
  };

  const sendCode = async (): Promise<string | null> => {
    setError(null);
    setIsLoading(true);
    try {
      const formatted = normalizeIsraeliPhone(phoneNumber);
      const verifier = getVerifier();
      const result = await signInWithPhoneNumber(auth, formatted, verifier);
      setConfirmationResult(result);
      return null;
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const msg = (err as { message?: string })?.message ?? '';
      let resolved: string;
      if (code === 'auth/invalid-phone-number') resolved = 'מספר טלפון לא תקין.';
      else if (code === 'auth/too-many-requests')
        resolved = 'יותר מדי ניסיונות. נסה שוב מאוחר יותר.';
      else if (code === 'auth/quota-exceeded') resolved = 'המערכת עמוסה. נסה שוב מאוחר יותר.';
      else resolved = `שגיאה בשליחת קוד אימות. ${msg || 'נסה שוב.'}`;
      setError(resolved);
      verifierRef.current?.clear();
      verifierRef.current = null;
      return resolved;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendCode();
  };

  if (confirmationResult) {
    return (
      <OTPVerificationForm
        confirmationResult={confirmationResult}
        phoneNumber={phoneNumber}
        onBack={() => {
          setConfirmationResult(null);
          setPhoneNumber('');
          setError(null);
        }}
        onResend={sendCode}
      />
    );
  }

  return (
    <>
      <LoginPage
        calm
        title={`היי, ${timeGreeting()}.`}
        subtitle="הזן את מספר הטלפון שלך ונשלח לך קוד כניסה."
        error={error ?? authError}
        brandTitle="ELKA · פורטל צוות"
        valueProps={['דיווח שעות, סידור עבודה', 'ותלושי שכר —', 'הכל במקום אחד.']}
        footerText={`© ${currentYear} פורטל עובדים אלקה`}
      >
        <form onSubmit={handleSendCode} className="mt-6 space-y-4">
          <div>
            <label
              htmlFor="phone"
              className="text-muted mb-2 block text-[11px] font-bold tracking-[0.6px] uppercase"
            >
              טלפון
            </label>
            <input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="050-000-0000"
              required
              disabled={isLoading}
              dir="ltr"
              className="rounded-control border-line-strong text-ink bg-surface placeholder:text-sub focus:border-brand min-h-[52px] w-full border px-4 py-3 text-center text-lg tracking-wider tabular-nums transition-colors disabled:opacity-60"
            />
            <p className="text-sub mt-1.5 text-center text-xs">פורמט: 050-000-0000 או 0501234567</p>
          </div>

          <button
            type="submit"
            disabled={isLoading || !phoneNumber.trim()}
            className="rounded-control bg-brand hover:bg-brand-ink flex min-h-[52px] w-full items-center justify-center gap-3 py-3.5 text-base font-bold text-white transition-[background-color,transform] active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100"
          >
            {isLoading ? <LoadingSpinner size="sm" centered={false} /> : 'שלח קוד'}
          </button>
        </form>

        <p className="text-sub mt-5 text-center text-xs">נתקלת בבעיה? פנה למנהל המערכת</p>
      </LoginPage>

      <div id={RECAPTCHA_ID} ref={containerRef} className="fixed" />
    </>
  );
}
